import React, { useState, useEffect, useRef } from 'react'
import axios, { AxiosRequestHeaders } from 'axios'

export enum StatusServicesRequestEnum {
  COMPLETED = 'COMPLETED',
  PENDING_PAYMENT = 'PENDING',
  PENDING_SIGNATURE_FILE = 'PENDING_SIGNATURE_FILE',
  PENDING_UPLOAD_FILE = 'PENDING_UPLOAD_FILE',
  CANCELED = 'CANCELED'
}

export interface DocumentRequirement {
  key: string
  label: string
  required: boolean
  signatory: 'director' | 'shareholder' | 'agent' | null
  file?: File
  status?: StatusServicesRequestEnum
}

interface Props {
  openDialog: boolean
  onOpenDialogChange: (open: boolean) => void
  idServicesRequest: string
  fetchHeaders: AxiosRequestHeaders
  requiredDocuments: DocumentRequirement[]
  onDocumentsChange: (docs: DocumentRequirement[]) => void
  onApprove: () => void
  canApprove: boolean
}

export const DialogUploadRequest: React.FC<Props> = ({
  openDialog,
  onOpenDialogChange,
  idServicesRequest,
  fetchHeaders,
  requiredDocuments,
  onDocumentsChange,
  onApprove,
  canApprove
}) => {
  const [localDocs, setLocalDocs] = useState<DocumentRequirement[]>([])
  const isSyncingRef = useRef(false)

  const mapDocumentsWithStatus = (docs: DocumentRequirement[]) =>
    docs.map(doc => ({
      ...doc,
      status: doc.status ?? StatusServicesRequestEnum.PENDING_UPLOAD_FILE
    }))

  // Inicializa quando documentos mudam
  useEffect(() => {
    if (isSyncingRef.current) {
      isSyncingRef.current = false
      return
    }

    const docsWithStatus = mapDocumentsWithStatus(requiredDocuments)
    setLocalDocs(docsWithStatus)

    const needsSync =
      docsWithStatus.length !== requiredDocuments.length ||
      docsWithStatus.some((doc, index) => {
        const original = requiredDocuments[index]
        if (!original) return true
        return (
          original.status !== doc.status ||
          original.file !== doc.file ||
          original.key !== doc.key
        )
      })

    if (needsSync) {
      isSyncingRef.current = true
      onDocumentsChange(docsWithStatus)
    }
  }, [requiredDocuments, onDocumentsChange])

  const updateDocuments = (updater: (docs: DocumentRequirement[]) => DocumentRequirement[]) => {
    setLocalDocs(prevDocs => {
      const nextDocs = updater(prevDocs)
      isSyncingRef.current = true
      onDocumentsChange(nextDocs)
      return nextDocs
    })
  }

  const handleFileSelect = (key: string, file: File) => {
    updateDocuments(docs =>
      docs.map(d =>
        d.key === key ? { ...d, file, status: StatusServicesRequestEnum.PENDING_UPLOAD_FILE } : d
      )
    )
  }

  const handleUpload = async (docKey: string) => {
    const doc = localDocs.find(d => d.key === docKey)
    if (!doc?.file) return

    const form = new FormData()
    form.append('file', doc.file)

    try {
      await axios.post(
        `/services/${idServicesRequest}/documents/${docKey}`,
        form,
        { headers: { ...fetchHeaders, 'Content-Type': 'multipart/form-data' } }
      )
      const nextStatus = doc.signatory
        ? StatusServicesRequestEnum.PENDING_SIGNATURE_FILE
        : StatusServicesRequestEnum.COMPLETED
      updateDocuments(docs =>
        docs.map(d => (d.key === docKey ? { ...d, status: nextStatus } : d))
      )
    } catch (error) {
      console.error('Upload failed', error)
    }
  }

  const handleSign = async (docKey: string) => {
    try {
      await axios.post(
        `/services/${idServicesRequest}/documents/${docKey}/sign`,
        {},
        { headers: fetchHeaders }
      )
      updateDocuments(docs =>
        docs.map(d =>
          d.key === docKey ? { ...d, status: StatusServicesRequestEnum.COMPLETED } : d
        )
      )
    } catch (error) {
      console.error('Sign request failed', error)
    }
  }

  if (!openDialog) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-11/12 max-w-2xl p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-semibold">Upload de Documentos</h2>
          <button
            className="text-gray-500 hover:text-gray-800 focus:outline-none"
            onClick={() => onOpenDialogChange(false)}
          >
            ✕
          </button>
        </div>

        {/* Body: grid de documentos */}
        <div className="grid grid-cols-12 gap-x-6 gap-y-4 max-h-[60vh] overflow-y-auto pb-4">
          {localDocs.map(doc => (
            <React.Fragment key={doc.key}>
              {/* Label + status */}
              <div className="col-span-7">
                <label className="flex flex-col">
                  <span className={doc.required ? 'font-medium text-gray-900' : 'text-gray-700'}>
                    {doc.label}{doc.required && <span className="text-red-600"> *</span>}
                  </span>
                  <span className="mt-1 text-sm text-gray-500">Status: {doc.status}</span>
                </label>
              </div>

              {/* Ações de upload/sign */}
              <div className="col-span-5 flex items-center gap-2">
                <input
                  type="file"
                  accept="application/pdf"
                  className="block w-full text-sm text-gray-700 border border-gray-300 rounded px-2 py-1 focus:outline-none"
                  disabled={doc.status !== StatusServicesRequestEnum.PENDING_UPLOAD_FILE}
                  onChange={e => e.target.files?.[0] && handleFileSelect(doc.key, e.target.files[0])}
                />
                <button
                  className="px-3 py-1 bg-blue-600 text-white rounded disabled:opacity-50 focus:outline-none"
                  disabled={!doc.file || doc.status !== StatusServicesRequestEnum.PENDING_UPLOAD_FILE}
                  onClick={() => handleUpload(doc.key)}
                >
                  Enviar
                </button>
                {doc.signatory && doc.status === StatusServicesRequestEnum.PENDING_SIGNATURE_FILE && (
                  <button
                    className="px-3 py-1 bg-green-600 text-white rounded focus:outline-none"
                    onClick={() => handleSign(doc.key)}
                  >
                    Assinar
                  </button>
                )}
              </div>
            </React.Fragment>
          ))}
        </div>

        {/* Footer: Aprovar */}
        <div className="flex justify-end border-t pt-4">
          <button
            onClick={onApprove}
            disabled={!canApprove}
            className="px-6 py-2 bg-indigo-600 text-white rounded disabled:opacity-50 focus:outline-none"
          >
            Aprovar Empresa
          </button>
        </div>
      </div>
    </div>
  )
}
